import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AutomatedFilingCard = ({ lodgment, onSubmit, onRetry, onViewDetails }) => {
  const [submitting, setSubmitting] = useState(false);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return 'CheckCircle2';
      case 'submitted':
        return 'Clock';
      case 'pending':
        return 'AlertCircle';
      case 'failed': case'rejected':
        return 'XCircle';
      default:
        return 'FileText';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'var(--color-success)';
      case 'submitted':
        return 'var(--color-info)';
      case 'pending':
        return 'var(--color-warning)';
      case 'failed': case'rejected':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'submitted':
        return 'Submitted';
      case 'pending':
        return 'Pending Submission';
      case 'failed':
        return 'Failed';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return `K${parseFloat(amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit?.(lodgment?.id);
    } finally {
      setSubmitting(false);
    }
  };

  const getFilingTypeLabel = (type) => {
    switch (type) {
      case 'nasfund':
        return 'NASFUND Contribution';
      case 'swt':
        return 'SWT Filing';
      case 'annual_reconciliation':
        return 'Annual Reconciliation';
      default:
        return type;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 transition-smooth hover:shadow-md">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <Icon 
            name={getStatusIcon(lodgment?.lodgmentStatus)} 
            size={20} 
            color={getStatusColor(lodgment?.lodgmentStatus)} 
          />
          <div className="flex-1 min-w-0">
            <h4 className="font-body font-semibold text-sm text-foreground mb-1">
              {getFilingTypeLabel(lodgment?.filingType)}
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              Period: {lodgment?.filingPeriod}
            </p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="data-text font-medium text-foreground">
                {formatCurrency(lodgment?.totalAmount)}
              </span>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">
                {lodgment?.employeeCount} employees
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: `${getStatusColor(lodgment?.lodgmentStatus)}15` }}>
          <span className="text-xs font-medium" style={{ color: getStatusColor(lodgment?.lodgmentStatus) }}>
            {getStatusLabel(lodgment?.lodgmentStatus)}
          </span>
        </div>
      </div>

      {lodgment?.portalReferenceNumber && (
        <div className="bg-muted/50 rounded-md p-2 mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs caption text-muted-foreground">Reference:</span>
            <span className="text-xs data-text font-medium text-foreground">
              {lodgment?.portalReferenceNumber}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-1 mb-3">
        {lodgment?.generatedAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="FileCheck" size={14} color="currentColor" />
            <span>Generated: {formatDate(lodgment?.generatedAt)} {formatTime(lodgment?.generatedAt)}</span>
          </div>
        )}
        {lodgment?.submittedAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Send" size={14} color="currentColor" />
            <span>Submitted: {formatDate(lodgment?.submittedAt)} {formatTime(lodgment?.submittedAt)}</span>
          </div>
        )}
        {lodgment?.confirmedAt && (
          <div className="flex items-center gap-2 text-xs text-success">
            <Icon name="CheckCircle2" size={14} color="currentColor" />
            <span>Confirmed: {formatDate(lodgment?.confirmedAt)} {formatTime(lodgment?.confirmedAt)}</span>
          </div>
        )}
      </div>

      {lodgment?.errorMessage && (
        <div className="bg-error/10 border border-error/20 rounded-md p-2 mb-3">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
            <p className="text-xs text-error flex-1">{lodgment?.errorMessage}</p>
          </div>
          {lodgment?.retryCount > 0 && (
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Retry attempts: {lodgment?.retryCount}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        {lodgment?.lodgmentStatus === 'pending' && (
          <Button
            variant="primary"
            size="sm"
            iconName="Send"
            iconPosition="left"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit to Portal'}
          </Button>
        )}
        {(lodgment?.lodgmentStatus === 'failed' || lodgment?.lodgmentStatus === 'rejected') && (
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onRetry?.(lodgment?.id)}
          >
            Retry Submission
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onViewDetails?.(lodgment)}
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

export default AutomatedFilingCard;
